import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Loader2, Facebook, Instagram, Twitter, Linkedin, Youtube, Share2, Mail, Phone } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSecondaryActions, getPrimaryActions } from '../../utils/actionFilters';

const DataTable = ({ 
  data = [], 
  columns = [], 
  error = null, 
  emptyMessage = 'No data found',
  onRowAction = () => {},
  cardView = false, // Allow forcing card view
  cardTitle = null, // Function to get card title
  cardSubtitle = null, // Function to get card subtitle
  actionState = () => ({}), // Function to get action-specific state (like loading)
  getSocials = null, // Function to extract social media links from row data
  getContacts = null // Function to extract email/phone data from row data
}) => {
  const { isRTL } = useLanguage();

  // Get social media icon
  const getSocialIcon = (platform) => {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes('facebook')) return <Facebook className="h-4 w-4" />;
    if (platformLower.includes('instagram')) return <Instagram className="h-4 w-4" />;
    if (platformLower.includes('twitter') || platformLower.includes('x.com')) return <Twitter className="h-4 w-4" />;
    if (platformLower.includes('linkedin')) return <Linkedin className="h-4 w-4" />;
    if (platformLower.includes('youtube')) return <Youtube className="h-4 w-4" />;
    return <Share2 className="h-4 w-4" />;
  };

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8 text-red-600">
          <p className="text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  // Get action column for cards
  const actionColumn = columns.find(col => col.actions);
  const dataColumns = columns.filter(col => !col.actions);

  return (
    <>
      {/* Desktop Table View - Hidden on mobile, or hidden when cardView is true */}
      {!cardView && (
        <div className="hidden md:block">
          <Card>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      {columns.map((column, index) => (
                        <th 
                          key={column.key || index}
                          className={`py-3 px-4 font-medium text-sm ${
                            column.actions 
                              ? (isRTL ? 'text-left' : 'text-right')
                              : column.align === 'right' 
                              ? 'text-right' 
                              : column.align === 'center' 
                              ? 'text-center' 
                              : 'text-left'
                          }`}
                        >
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, rowIndex) => (
                      <tr key={row.id || rowIndex} className="border-b hover:bg-gray-50">
                        {columns.map((column, colIndex) => (
                          <td 
                            key={`${rowIndex}-${column.key || colIndex}`}
                            className={`py-3 px-4 text-sm ${
                              column.actions 
                                ? (isRTL ? 'text-left' : 'text-right')
                                : column.align === 'right' 
                                ? 'text-right' 
                                : column.align === 'center' 
                                ? 'text-center' 
                                : 'text-left'
                            } ${column.fontWeight || ''}`}
                          >
                            {column.actions ? (
                              <div className={`flex items-center gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                                {column.actions.map((action, actionIndex) => (
                                  <Button
                                    key={actionIndex}
                                    variant={action.variant || 'outline'}
                                    size={action.size || 'sm'}
                                    onClick={() => onRowAction(action.key, row)}
                                    disabled={action.disabled}
                                    className={action.className || ''}
                                    title={action.title}
                                  >
                                    {action.icon && <span className="mr-2">{action.icon}</span>}
                                    {typeof action.label === 'function' ? action.label(row) : action.label}
                                  </Button>
                                ))}
                              </div>
                            ) : column.render ? (
                              column.render(row[column.key], row, rowIndex)
                            ) : (
                              row[column.key]
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Card View - Mobile by default, or always when cardView is true */}
      <div className={`${cardView ? 'grid grid-cols-1 lg:grid-cols-3 gap-4' : 'md:hidden space-y-4'}`}>
        {data.map((row, rowIndex) => (
          <Card key={row.id || rowIndex} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="space-y-1 flex-1">
                  {cardTitle && (
                    <CardTitle className="text-sm line-clamp-2">
                      {cardTitle(row)}
                    </CardTitle>
                  )}
                  {cardSubtitle && (
                    <p className="text-xs text-muted-foreground">
                      {cardSubtitle(row)}
                    </p>
                  )}
                  {!cardTitle && !cardSubtitle && dataColumns.length > 0 && (
                    <>
                      <CardTitle className="text-sm line-clamp-2">
                        {dataColumns[0].render ? 
                          dataColumns[0].render(row[dataColumns[0].key], row, rowIndex) : 
                          row[dataColumns[0].key]
                        }
                      </CardTitle>
                      {dataColumns[1] && (
                        <p className="text-xs text-muted-foreground">
                          {dataColumns[1].render ? 
                            dataColumns[1].render(row[dataColumns[1].key], row, rowIndex) : 
                            row[dataColumns[1].key]
                          }
                        </p>
                      )}
                    </>
                  )}
                </div>
                
                {/* Social Media, Email, and Phone Icons */}
                {(getSocials || getContacts) && (() => {
                  const socials = getSocials ? getSocials(row) : null;
                  const contacts = getContacts ? getContacts(row) : null;
                  const hasEmail = contacts?.emails && contacts.emails.length > 0;
                  const hasPhone = contacts?.phones && contacts.phones.length > 0;
                  const hasSocials = socials && Object.keys(socials).length > 0;
                  
                  if (!hasSocials && !hasEmail && !hasPhone) {
                    return null;
                  }
                  
                  return (
                    <div className="flex items-center gap-1 ml-2">
                      {/* Email Icon */}
                      {hasEmail && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowAction('contacts', row);
                          }}
                          className="p-1.5 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
                          title="View email contacts"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                      
                      {/* Phone Icon */}
                      {hasPhone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowAction('contacts', row);
                          }}
                          className="p-1.5 hover:bg-green-50 hover:text-green-600 rounded-md transition-colors"
                          title="View phone contacts"
                        >
                          <Phone className="h-4 w-4" />
                        </button>
                      )}
                      
                      {/* Social Media Icons */}
                      {hasSocials && Object.entries(socials).map(([platform, url], idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(url, '_blank');
                          }}
                          className="p-1.5 hover:bg-purple-50 hover:text-purple-600 rounded-md transition-colors"
                          title={`Visit ${platform}`}
                        >
                          {getSocialIcon(platform)}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Show additional data columns if any */}
              {dataColumns.length > 2 && (
                <div className="mb-3 space-y-1">
                  {dataColumns.slice(2).map((column, colIndex) => (
                    <div key={colIndex} className="text-xs">
                      <span className="font-medium">{column.header}: </span>
                      <span className="text-muted-foreground">
                        {column.render ? 
                          column.render(row[column.key], row, rowIndex) : 
                          row[column.key]
                        }
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Action buttons */}
              {actionColumn && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  {/* Secondary actions (delete, archive, contacts) */}
                  <div className="flex gap-2">
                    {getSecondaryActions(actionColumn.actions, row).map((action, actionIndex) => {
                      const state = actionState(row, action);
                      return (
                        <Button
                          key={actionIndex}
                          variant={action.variant || 'outline'}
                          size={action.size || 'sm'}
                          className={action.className || ''}
                          onClick={() => onRowAction(action.key, row)}
                          disabled={action.disabled || state.loading}
                          title={action.title}
                        >
                          {state.loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                          {!state.loading && action.icon && <span className="mr-2">{action.icon}</span>}
                          {typeof action.label === 'function' ? action.label(row) : action.label}
                        </Button>
                      );
                    })}
                  </div>
                  
                  {/* Primary action (submit button) */}
                  <div className="flex gap-2">
                    {getPrimaryActions(actionColumn.actions, row).map((action, actionIndex) => {
                      const state = actionState(row, action);
                      return (
                        <Button
                          key={actionIndex}
                          variant={action.variant || 'outline'}
                          size={action.size || 'sm'}
                          className={action.className || ''}
                          onClick={() => onRowAction(action.key, row)}
                          disabled={action.disabled || state.loading}
                          title={action.title}
                          {...(action.key === 'checkWebsite' ? { 'data-scan-button': 'true' } : {})}
                        >
                          {state.loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                          {!state.loading && action.icon && <span className="mr-2">{action.icon}</span>}
                          {typeof action.label === 'function' ? action.label(row) : action.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default DataTable; 